import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactElement,
} from 'react';
import styled, { keyframes } from 'styled-components';
import { Popover2025, useDelayedHover } from '@ardoq/popovers';
import { FlexBox } from '@ardoq/layout';
import { easingTokens, spacing, transitionTokens } from '@ardoq/design-tokens';
import { permissionsOperations } from '@ardoq/access-control';
import { MiniComponentOverview } from './MiniComponentOverview';
import {
  HOVER_CLOSE_GRACE_MS,
  HOVER_OPEN_DELAY_MS,
  PREVIEW_PLACEMENT,
} from './miniOverviewConsts';
import type {
  ComponentHoverPreviewProps,
  HoverPreviewTriggerProps,
} from './types';

const popoverFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const AnimatedPopover = styled(Popover2025)`
  margin-block: ${spacing.s16};
  animation: ${popoverFadeIn} ${transitionTokens.medium}
    ${easingTokens.cubicOut} backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PreviewCard = styled(FlexBox)`
  overflow-wrap: break-word;
`;

export const ComponentHoverPreview = ({
  state,
  isActive,
  children,
  i18nLocale,
  permissionContext = permissionsOperations.createEmptyPermissionContext(),
  onExpand,
  onHoverChange,
  onOpen,
  skeletonVariant = 'default',
  placement = PREVIEW_PLACEMENT,
  className,
  renderTrigger,
}: ComponentHoverPreviewProps): ReactElement => {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const setAnchorRef = useCallback<HoverPreviewTriggerProps['ref']>(node => {
    anchorRef.current = node;
  }, []);
  const { isOpen, handleMouseEnter, handleMouseLeave, handleClose } =
    useDelayedHover({
      openDelayMs: HOVER_OPEN_DELAY_MS,
      closeDelayMs: HOVER_CLOSE_GRACE_MS,
    });

  useEffect(() => {
    if (isActive === undefined) return;
    if (isActive) {
      handleMouseEnter();
    } else {
      handleMouseLeave();
    }
  }, [isActive, handleMouseEnter, handleMouseLeave]);

  const openOnHover = () => {
    onHoverChange?.(true);
    onOpen?.();
    handleMouseEnter();
  };

  const handleTriggerLeave = () => {
    onHoverChange?.(false);
    handleMouseLeave();
  };

  const handleCardEnter = () => {
    onHoverChange?.(true);
    handleMouseEnter();
  };

  const handleCardLeave = () => {
    onHoverChange?.(false);
    handleMouseLeave();
  };

  const handleExpand = onExpand
    ? () => {
        onHoverChange?.(false);
        handleClose();
        onExpand();
      }
    : undefined;

  const triggerProps: HoverPreviewTriggerProps = {
    ref: setAnchorRef,
    'aria-haspopup': 'dialog',
    'aria-expanded': isOpen,
    'aria-controls': id,
    onMouseEnter: openOnHover,
    onMouseLeave: handleTriggerLeave,
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <span
          {...triggerProps}
          className={className}
          role="button"
          tabIndex={0}
        >
          {children}
        </span>
      )}
      <AnimatedPopover
        id={id}
        isOpen={isOpen}
        onClose={handleClose}
        onClickOutside={handleClose}
        anchorRef={anchorRef}
        placement={placement}
      >
        <PreviewCard
          flexDirection="column"
          backgroundColor="surfaceDefault"
          borderRadius="r8"
          boxShadow="shadowM"
          padding="medium"
          onMouseOver={handleCardEnter}
          onMouseLeave={handleCardLeave}
        >
          <MiniComponentOverview
            state={state}
            permissionContext={permissionContext}
            onExpand={handleExpand}
            i18nLocale={i18nLocale}
            skeletonVariant={skeletonVariant}
          />
        </PreviewCard>
      </AnimatedPopover>
    </>
  );
};
